/**
 * Convierte el formato Lexical JSON (usado por Payload CMS) a HTML
 * 
 * @param {Object|string} lexicalData - Datos en formato Lexical o HTML string
 * @returns {string} - HTML string
 */
const lexicalToHtml = (lexicalData) => {
  // Si es undefined, null o vacío
  if (!lexicalData) {
    return '';
  }

  // Si ya es un string (HTML antiguo), devolverlo tal cual
  if (typeof lexicalData === 'string') {
    return lexicalData;
  }

  // Si es un objeto Lexical, procesarlo
  if (typeof lexicalData === 'object' && lexicalData.root) {
    return convertNodeToHtml(lexicalData.root);
  }

  return '';
};

/**
 * Convierte un nodo Lexical a HTML
 */
const convertNodeToHtml = (node) => {
  if (!node || !node.children) {
    return '';
  }

  return node.children.map(child => convertChildToHtml(child)).join('');
};

/**
 * Convierte un hijo (child) de Lexical a HTML
 */
const convertChildToHtml = (child) => {
  // Texto plano
  if (child.type === 'text') {
    let text = child.text || '';
    
    // Aplicar formatos (bold, italic, underline, etc.)
    if (child.format) {
      // Bold (1)
      if (child.format & 1) {
        text = `<strong>${text}</strong>`;
      }
      // Italic (2)
      if (child.format & 2) {
        text = `<em>${text}</em>`;
      }
      // Underline (8)
      if (child.format & 8) {
        text = `<u>${text}</u>`;
      }
      // Strikethrough (4)
      if (child.format & 4) {
        text = `<s>${text}</s>`;
      }
      // Code (16)
      if (child.format & 16) {
        text = `<code>${text}</code>`;
      }
    }
    
    return text;
  }

  // Salto de línea
  if (child.type === 'linebreak') {
    return '<br/>';
  }

  // Link
  if (child.type === 'link') {
    const url = child.url || '#';
    const rel = child.rel || '';
    const target = child.target || '';
    const content = child.children ? child.children.map(c => convertChildToHtml(c)).join('') : '';
    
    return `<a href="${url}"${target ? ` target="${target}"` : ''}${rel ? ` rel="${rel}"` : ''}>${content}</a>`;
  }

  // Párrafo
  if (child.type === 'paragraph') {
    const content = child.children ? child.children.map(c => convertChildToHtml(c)).join('') : '';
    return `<p>${content}</p>`;
  }

  // Headings
  if (child.type === 'heading') {
    const tag = child.tag || 'h2';
    const content = child.children ? child.children.map(c => convertChildToHtml(c)).join('') : '';
    return `<${tag}>${content}</${tag}>`;
  }

  // Lista desordenada
  if (child.type === 'list' && child.listType === 'bullet') {
    const content = child.children ? child.children.map(c => convertChildToHtml(c)).join('') : '';
    return `<ul>${content}</ul>`;
  }

  // Lista ordenada
  if (child.type === 'list' && child.listType === 'number') {
    const content = child.children ? child.children.map(c => convertChildToHtml(c)).join('') : '';
    return `<ol>${content}</ol>`;
  }

  // Item de lista
  if (child.type === 'listitem') {
    const content = child.children ? child.children.map(c => convertChildToHtml(c)).join('') : '';
    return `<li>${content}</li>`;
  }

  // Quote
  if (child.type === 'quote') {
    const content = child.children ? child.children.map(c => convertChildToHtml(c)).join('') : '';
    return `<blockquote>${content}</blockquote>`;
  }

  // Code block
  if (child.type === 'code') {
    const content = child.children ? child.children.map(c => convertChildToHtml(c)).join('') : '';
    return `<pre><code>${content}</code></pre>`;
  }

  // Por defecto, procesar children si existen
  if (child.children) {
    return child.children.map(c => convertChildToHtml(c)).join('');
  }

  return '';
};

export default lexicalToHtml;

